import { Box, Grid, Slide, Typography } from "@mui/material";
import { useGetOnboardingStatusesQuery } from "api/api-origination";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import { ACCOUNTS, DEFAULT_ROWS, INCOMLEND_INTERNAL_LINK } from "codes";
import CLDisplay from "components/CLRequest/CLDisplay";
import DataTablev2 from "components/Common/DataTablev2";
import Layout from "components/Common/Layout";
import SkeletonLoad from "components/Common/SkeletonLoad";
import useWidth from "helpers/useWidth";
import useIsMobile from "hooks/useIsMobile";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { clearAll } from "redux/reducers/searchReducer";
import NoData from "components/Common/NoData";
import styles from "../sales.module.scss";

interface OnboardingTableParams {
  seller: string;
  seller_internal_name: string;
  seller_country: string;
  seller_is_identified: boolean | null;
  seller_details_completed: boolean;
  seller_documents_completed: boolean;
  seller_has_bank_account: boolean;
  buyer: string;
  buyer_internal_name: string;
  buyer_details_completed: boolean;
  buyer_documents_completed: boolean;
  buyer_is_identified: boolean | null;
  buyer_country: string;
  onboarding_status: boolean;
  internal_status: string;
}

const OnboardingsPage = () => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const searchValue = useAppSelector((state) => state.search.value);
  const hasSearchedOnce = useRef(false);
  const [queryParams, setQueryParams] = useState({
    per_page: 10,
    page: 1,
    search: searchValue || undefined,
  });
  const [filters, setFilters] = useState<Record<string, any>>({});

  useEffect(() => {
    dispatch(clearAll());
  }, [dispatch]);

  useEffect(() => {
    if (searchValue && !hasSearchedOnce.current) {
      hasSearchedOnce.current = true;
    }
    if (hasSearchedOnce.current) {
      setQueryParams((prev) => ({ ...prev, page: 1, search: searchValue || undefined }));
    }
  }, [searchValue]);

  const { data: onboardings, isFetching, isLoading, refetch } = useGetOnboardingStatusesQuery(
    { 
      onbordpipeparams: `?${new URLSearchParams(
        Object.fromEntries(
          Object.entries(queryParams)
            .filter(([_, value]) => value !== undefined)
        ) as Record<string, string>
      )}` 
    },
    { refetchOnMountOrArgChange: true }
  );

  const handleClearFilters = () => {
    // Explicitly reset query params to per_page: 10 and page: 1
    setQueryParams({
      per_page: 10, // Hardcoded to 10
      page: 1,
      search: undefined,
    });
    setFilters({}); // Clear filters state
    dispatch(clearAll()); // Clear search state
    refetch(); // Trigger API refetch with per_page=10 and page=1
  };

  const handleFilterSubmit = (filtersString: string) => {
    const filtersObj = JSON.parse(filtersString);
    setFilters(filtersObj);
    const newFilters: Record<string, string> = {};
    for (const key in filtersObj) {
      if (filtersObj[key]?.keywords) {
        if (key === "created_at" && typeof filtersObj[key].keywords === "string") {
          newFilters[key] = filtersObj[key].keywords;
        } else if (Array.isArray(filtersObj[key].keywords)) {
          newFilters[key] = filtersObj[key].keywords.join(",");
        }
      }
    }
    setQueryParams((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (newPage: number, newPerPage: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: newPage,
      per_page: newPerPage
    }));
  };

  const handleRefetch = async () => {
    await refetch();
    setQueryParams({
      per_page: 10,
      page: 1,
      search: undefined
    });
    dispatch(clearAll());
  };

  const formatOnboardingsForTable = (data: OnboardingTableParams[]) => {
    return data?.map((item) => ({
      ...item,
    })) || [];
  };

  const formattedData = formatOnboardingsForTable(onboardings?.onboarding_details);
  const { data: currUser } = useGetCurrentUserQuery();
  const rowsPerPage = currUser?.settings?.rowsPerPage ?? DEFAULT_ROWS;
  const ref = useRef<HTMLDivElement>(null);
  const width = useWidth(ref);
  const headerLength = Object.keys(formattedData[0] || {}).length;
  const defaultColumnWidth = width ? (width / headerLength) * 1.6 : 200;

  return (
    <Slide in direction="left">
      <Box sx={{ display: "flex" }} flexGrow={1}>
        <Layout
          title="Onboardings"
          textHeadingOptions={{
            fontWeight: "bolder",
            level: 2,
            fontSize: isMobile ? "1.2em" : "1.5em",
          }}
          className={styles.layout}
          headerConfig={{
            left: 4,
            mid: 0,
            right: 4,
            syncAction: handleRefetch,
            xs: {
              left: 6,
              mid: 0,
              right: 6,
            },
            alignItems: "center",
          }}
          mode="default"
        >
          <Grid item xs={12}>
            <Grid item xs={12} ref={ref}>
              {(isFetching || isLoading) && <SkeletonLoad bars={10} />}
              {!isFetching && formattedData.length > 0 ? (
                <DataTablev2
                  disableFilters={false}
                  clearFilters={handleClearFilters}
                  filterColumnTableName={onboardings}
                  pagination={onboardings?.pagination}
                  data={formattedData}
                  defaultColumnWidth={defaultColumnWidth}
                  rowsPerPage={rowsPerPage}
                  onPaginationChange={handlePageChange}
                  onFilterSubmit={handleFilterSubmit}
                  initialFilters={{}}
                  filters={filters}
                  setFilters={setFilters}
                  toBeHidden={[
                    "buyer_is_identified",
                    "seller_is_identified",
                    "seller_internal_name",
                    "buyer_internal_name",
                  ]}
                  customColumns={{
                    internal_status: {
                      internal_status: "CL Status",
                      minWidth: isMobile ? "100%" : 120,
                      type: "text",
                      renderCell: (params: any) => (
                        <Grid container justifyContent={isMobile ? "left" : "center"}>
                          <Grid item>
                            <CLDisplay status={params.value} />
                          </Grid>
                        </Grid>
                      ),
                    },
                    onboarding_status: {
                      onboarding_status: "Onboarding Status",
                      minWidth: 200,
                      type: "boolean",
                      renderCell: (params: any) => (
                        <Typography
                          sx={{
                            background: params.value ? "#9FCE63" : "red",
                            padding: "6px 16px",
                            borderRadius: "10px",
                            color: "#ffffff",
                            fontWeight: "600",
                            fontSize: "1em",
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          {params.value ? "Submitted" : "Pending"}
                        </Typography>
                      ),
                    },
                    buyer: {
                      buyer: "Buyer",
                      minWidth: 200,
                      link: {
                        href: (params: OnboardingTableParams) =>
                          `${INCOMLEND_INTERNAL_LINK}${ACCOUNTS}/${params.buyer_internal_name}`,
                        target: "_self",
                      },
                    },
                    buyer_country: {
                      buyer_country: "Buyer Country",
                      minWidth: 200,
                      type: "text",
                      headerProps: { sx: { maxWidth: "100%" } },
                    },
                    buyer_details_completed: {
                      buyer_details_completed: "Buyer Details Completed",
                      minWidth: 200,
                      type: "boolean",
                      renderCell: (params: any) => (
                        <Typography
                          sx={{
                            background: params.value ? "#9FCE63" : "red",
                            padding: "6px 16px",
                            borderRadius: "10px",
                            color: "#ffffff",
                            fontWeight: "600",
                            fontSize: "1em",
                            maxWidth: "100%",
                            textAlign: "center",
                          }}
                        >
                          {params.value ? "Completed" : "Pending"}
                        </Typography>
                      ),
                    },
                    seller_has_bank_account: {
                      seller_has_bank_account: "Supplier Bank Account",
                      type: "boolean",
                      renderCell: (params: any) => (
                        <Typography
                          sx={{
                            background: params.value ? "#9FCE63" : "red",
                            padding: "6px 16px",
                            borderRadius: "10px",
                            color: "#ffffff",
                            fontWeight: "600",
                            fontSize: "1em",
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          {params.value ? "Completed" : "Pending"}
                        </Typography>
                      ),
                    },
                    seller_country: {
                      seller_country: "Supplier Country",
                      minWidth: 200,
                      type: "text",
                      headerProps: { sx: { maxWidth: "100%" } },
                    },
                    seller_details_completed: {
                      seller_details_completed: "Supplier Details Completed",
                      minWidth: 200,
                      type: "boolean",
                      renderCell: (params: any) => (
                        <Typography
                          sx={{
                            background: params.value ? "#9FCE63" : "red",
                            padding: "6px 16px",
                            borderRadius: "10px",
                            color: "#ffffff",
                            fontWeight: "600",
                            width: "100%",
                            textAlign: "center",
                            fontSize: "1em",
                          }}
                        >
                          {params.value ? "Completed" : "Pending"}
                        </Typography>
                      ),
                    },
                    seller_documents_completed: {
                      seller_documents_completed: "Supplier Documents Completed",
                      minWidth: 200,
                      type: "boolean",
                      renderCell: (params: any) => (
                        <Typography
                          sx={{
                            background: params.value ? "#9FCE63" : "red",
                            padding: "6px 16px",
                            borderRadius: "10px",
                            color: "#ffffff",
                            fontWeight: "600",
                            width: "100%",
                            textAlign: "center",
                            fontSize: "1em",
                          }}
                        >
                          {params.value ? "Completed" : "Pending"}
                        </Typography>
                      ),
                    },
                    buyer_documents_completed: {
                      buyer_documents_completed: "Buyer Documents Completed",
                      minWidth: 200,
                      type: "boolean",
                      renderCell: (params: any) => (
                        <Typography
                          sx={{
                            background: params.value ? "#9FCE63" : "red",
                            padding: "6px 16px",
                            borderRadius: "10px",
                            color: "#ffffff",
                            fontWeight: "600",
                            width: "100%",
                            textAlign: "center",
                            fontSize: "1em",
                          }}
                        >
                          {params.value ? "Completed" : "Pending"}
                        </Typography>
                      ),
                    },
                  }}
                  sx={{
                    table: {
                      minWidth: "100% !important",
                    },
                    "& .MuiTableCell-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              ) : (
                <NoData text="No Onboardings found" />
              )}
            </Grid>
          </Grid>
        </Layout>
      </Box>
    </Slide>
  );
};

export default OnboardingsPage;
